/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useEffect, useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import {DownloadTemplate} from '@ion/core/src/types/templates';
import {InfoIcon} from '@ion/dls/src/icons/InfoIcon';
import {IconButton, IconButtonLabelPlacement} from '../../components/buttons/IconButton';
import {SystemSaveExportIcon} from '../../icons/SystemSaveExportIcon';
import ConditionalRender from '../../render/ConditionalRender';
import {dlsAlt, dlsColor} from '../../theme/theme';
import useThemeContext from '../../theme/useThemeContext';
import {IconSize} from '../../types/types';
import Tooltip from '../tooltip/Tooltip';

const tooltip = makeStyles(theme => ({
  arrow : {
    color : dlsColor('grey1')
  },
  tooltip : {
    ...dlsAlt('components', 'iconTooltip'),
    fontSize : '12px !important'
  }
}));

export const ExportTooltip = function(props) {
  return <Tooltip arrow {...props} />;
};

// defines the admin container props ...
export type CSVExportButtonProps = {
  onExport: (params?: DownloadTemplate) => void,
  entity?: string,
  isExporting?: boolean,
  isIncludeTitle?: boolean,
  size?: IconSize,
  enableExport?: boolean,
  exportLimit?: boolean,
  disableExport?: boolean,
  hasNoRowSelection?: boolean,
  hasRowSelection?: boolean,
  devicePwdInfo?: boolean,
  customTooltip?: string,
  exportLabel?: string,
  showCurrentLabel?: boolean,
  currentLabelPlacement?: IconButtonLabelPlacement,
  disableTooltip?: boolean
};

/**
 * The CSVExportButton provides the Export Icon Button which provides the state
 * and labels common amongst CSV Exports.
 *
 * ```typescript
 *   import {CSVExportButton} from '../../components/buttons/';
 *
 *   <CSVExportButton isExporting={isExporting} onExport={onExport} />
 * ```
 */
export const CSVExportButton = ({
  onExport,
  entity,
  isExporting,
  isIncludeTitle = false,
  hasRowSelection = false,
  hasNoRowSelection = false,
  enableExport = false,
  exportLimit = false,
  disableExport = false,
  devicePwdInfo = false,
  size = IconSize.medium,
  customTooltip,
  exportLabel,
  showCurrentLabel = true,
  currentLabelPlacement = IconButtonLabelPlacement.STACKED,
  disableTooltip = false
}: CSVExportButtonProps) => {
  const [toolTipTitle, setToolTipTitle] = useState<string>('');
  const commonExport                    = translate('common:export', {returnObjects: true, entity});
  const {isCurrent: isCurrentTheme}     = useThemeContext();

  useEffect(() => {
    if (customTooltip && !isExporting && !enableExport) {
      return setToolTipTitle(customTooltip);
    }
    if (hasNoRowSelection && enableExport) {
      return setToolTipTitle(commonExport.emptyDisableTooltip);
    } if (enableExport) {
      return setToolTipTitle(commonExport.disableTooltip);
    } if (exportLimit && disableExport) {
      return setToolTipTitle(commonExport.noCapTooltip);
    } if (!isExporting && isIncludeTitle) {
      return setToolTipTitle(commonExport.exportAllWithTitleTooltip);
    } if (hasRowSelection) {
      return setToolTipTitle(commonExport.filteredTooltip);
    } if (!isExporting) {
      return setToolTipTitle(commonExport.tooltip);
    } else {
      return setToolTipTitle(commonExport.tooltipPending);
    }
  }, [
    isExporting,
    enableExport,
    exportLimit,
    disableExport,
    toolTipTitle,
    hasRowSelection,
    hasNoRowSelection,
    customTooltip
  ]);

  const button = (
    <div>
      <IconButton icon={<SystemSaveExportIcon size={size} />}
        disabled={isExporting || enableExport}
        isDisableBackgroundColor={false}
        label={isCurrentTheme ? exportLabel ?? commonExport.label : undefined}
        labelPlacement={currentLabelPlacement}
        hideLabel={isCurrentTheme && !showCurrentLabel}
        onClick={onExport} />
    </div>
  );

  return (
    <>

      <ConditionalRender condition={devicePwdInfo}>
        <Tooltip title={<span>{translate('admin-devices:devicePassword.dialog.tooltipInfo')}</span>}
          placement={'bottom'}
          classes={tooltip()}
          arrow>
          <span style={{marginTop: '12px'}}>
            <InfoIcon />
          </span>
        </Tooltip>
      </ConditionalRender>
      {isCurrentTheme || disableTooltip ? button : (
        <ExportTooltip title={toolTipTitle}>
          {button}
        </ExportTooltip>
      )}
    </>
  );
};

export default CSVExportButton;
